/**
 * Central registry for every DevKit tool.
 *
 * This is the single source of truth that drives:
 *   - Routing & metadata (slug → page)
 *   - /tools list page (filter, search, category groups)
 *   - Home page Popular Tools + Categories
 *   - Header Tools dropdown + Command Palette
 *   - Internal linking (Related Tools)
 *   - sitemap.xml generation
 *   - JSON-LD WebApplication / FAQPage / BreadcrumbList
 *   - PostHog tool_open event ingestion
 *
 * IMPORTANT — Adding a new tool:
 *   1. Append a ToolDefinition entry below.
 *   2. Mark `status: 'active'` once its page + content + analytics are wired.
 *   3. Re-run `pnpm sitemap` so sitemap.xml stays in sync.
 *
 * The `apps/web/src/pages/tools/*Page.tsx` files remain the tool UI. They
 * read SEO/related data from `getToolBySlug(slug)` instead of hardcoding.
 *
 * NOTE — string literals are template literals (backticks) so we never have
 * to escape apostrophes in marketing copy.
 */

export type ToolCategory =
  | 'json'
  | 'encoding'
  | 'security'
  | 'regex'
  | 'time'
  | 'text'
  | 'utility'
  | 'web'
  | 'code'
  | 'ai';

export interface ToolHowToStep {
  name: string;
  text: string;
}

export interface ToolExample {
  title: string;
  description?: string;
  input: string;
  output: string;
}

export interface ToolFAQ {
  question: string;
  answer: string;
}

export interface ToolDefinition {
  /** Stable identifier (kebab-case). Used as React key + PostHog tool_id. */
  id: string;
  /** URL slug — page lives at `/<slug>`. */
  slug: string;
  /** Human display name. */
  name: string;
  category: ToolCategory;
  /** Short tagline (1 sentence, used in tool cards). */
  shortDescription: string;
  /** Long-form description for the SEO body section (≥300 words). */
  longDescription: string;
  /** Search keywords for in-app search. */
  keywords: string[];

  /** SEO meta for `<title>` / `<meta>` / Twitter / OG. */
  seo: {
    /** ≤60 chars. */
    title: string;
    /** 150–160 chars. */
    description: string;
    keywords: string[];
  };

  /** Optional 1-line disclaimer (e.g. JWT decoder). */
  disclaimer?: string;

  /** Bullet list rendered under the tool. */
  features: string[];

  /** Ordered steps rendered as a HowTo block (also emitted as HowTo JSON-LD). */
  howToSteps: ToolHowToStep[];

  /** Realistic input→output examples. */
  examples: ToolExample[];

  /** FAQ entries (also emitted as FAQPage JSON-LD). */
  faqs: ToolFAQ[];

  /** Other tool slugs to surface under "Related Tools". */
  relatedTools: string[];

  /** Whether to surface on the Home page Popular block. */
  popular?: boolean;

  /** Lifecycle. `planned` items are kept for documentation but excluded from sitemap + UI. */
  status: 'active' | 'planned';
}

/* -------------------------------------------------------------------------- */
/* Categories                                                                 */
/* -------------------------------------------------------------------------- */

export interface CategoryMeta {
  id: ToolCategory;
  name: string;
  description: string;
}

export const CATEGORIES: CategoryMeta[] = [
  { id: `json`, name: `JSON Tools`, description: `Format, validate, minify, and convert JSON.` },
  { id: `encoding`, name: `Encoding Tools`, description: `Encode and decode text with Base64, URL, HTML, Hex, Binary.` },
  { id: `security`, name: `Security Tools`, description: `Decode JWTs, generate UUIDs, hash and verify data.` },
  { id: `regex`, name: `Regex Tools`, description: `Test and debug regular expressions.` },
  { id: `time`, name: `Time Tools`, description: `Convert Unix timestamps, build cron expressions.` },
  { id: `text`, name: `Text Tools`, description: `Compare, transform, and analyze text.` },
  { id: `utility`, name: `Utility Tools`, description: `Generators for UUIDs, passwords, and more.` },
];

/* -------------------------------------------------------------------------- */
/* Site-wide constants                                                        */
/* -------------------------------------------------------------------------- */

export const SITE = {
  name: `DevKit`,
  url: `https://dditzy.com`,
  tagline: `Fast. Private. Developer Tools.`,
  description: `Free online developer tools that respect your privacy. Format JSON, decode JWTs, encode Base64, and more — all in your browser.`,
  locale: `en_US`,
  twitterHandle: `@dditzy`,
} as const;

/* -------------------------------------------------------------------------- */
/* Tool definitions                                                          */
/* -------------------------------------------------------------------------- */

export const TOOLS: ToolDefinition[] = [
  /* ----------------------------- JSON ----------------------------- */
  {
    id: `json-formatter`,
    slug: `json-formatter`,
    name: `JSON Formatter`,
    category: `json`,
    shortDescription: `Format, beautify and validate JSON instantly in your browser.`,
    longDescription: `The JSON Formatter takes a minified, broken, or hard-to-read JSON string and rewrites it with consistent indentation so you can actually see the structure. It runs entirely in your browser using native JSON.parse, which means the response is instant, works offline, and never sends your data to a server. Use it when you copy a one-liner from an API response, a log file, or a curl output and need to read it. Use it before pasting into a config file to make sure you match your project\u2019s style. The formatter supports 2-space, 4-space, and tab indentation, and it can optionally sort all keys alphabetically so you can spot missing fields faster when comparing two payloads side by side. It is also a JSON validator: if your input is invalid, you will see a clean error message with the line and column of the first failure, and the output area will not silently swallow the issue. Unlike most online formatters, DevKit does not store, log, or transmit your JSON — your input is processed locally, which makes it safe to use on production payloads, tokens, or any data you would not want sitting on someone else's server.`,
    keywords: [`json formatter`, `json beautifier`, `format json`, `json pretty print`, `json formatter online`],
    seo: {
      title: `JSON Formatter Online — Free JSON Beautifier | DevKit`,
      description: `Format, beautify and validate JSON online. Fast, free and private — runs in your browser. 2, 4, or tab indent, optional key sort.`,
      keywords: [`json formatter`, `json beautifier`, `format json`, `json pretty print`, `json formatter online`, `json validator`],
    },
    features: [
      `100% client-side — JSON never leaves your browser`,
      `2-space, 4-space, or tab indentation`,
      `Optional alphabetical key sorting for diff-friendly output`,
      `Live JSON validation with line + column error reporting`,
      `One-click copy or download as .json`,
    ],
    howToSteps: [
      { name: `Paste your JSON`, text: `Paste any minified or messy JSON into the input area on the left.` },
      { name: `Pick indentation`, text: `Choose 2 spaces, 4 spaces, or a tab from the indent selector.` },
      { name: `Click Format`, text: `Press the Format button (or Validate to just check syntax) to produce clean, indented JSON on the right.` },
      { name: `Copy or download`, text: `Use Copy to grab the result for your clipboard, or Download to save it as a .json file.` },
    ],
    examples: [
      {
        title: `Minified API response`,
        description: `A single-line payload from a REST API becomes human-readable.`,
        input: `{"id":42,"name":"DevKit","tags":["json","beautifier","free"],"stats":{"runs":12832,"stars":502}}`,
        output:
          `{\n  "id": 42,\n  "name": "DevKit",\n  "stats": {\n    "runs": 12832,\n    "stars": 502\n  },\n  "tags": [\n    "json",\n    "beautifier",\n    "free"\n  ]\n}`,
      },
      {
        title: `Config file cleanup`,
        description: `Reformat a flat config without changing the data.`,
        input: `{"api":{"baseUrl":"https://api.example.com","timeout":3000},"feature":{"darkMode":true,"beta":false}}`,
        output:
          `{\n  "api": {\n    "baseUrl": "https://api.example.com",\n    "timeout": 3000\n  },\n  "feature": {\n    "darkMode": true,\n    "beta": false\n  }\n}`,
      },
    ],
    faqs: [
      {
        question: `Is the JSON Formatter free?`,
        answer: `Yes. DevKit is free to use with no signup, no account, and no usage limits. The formatter runs entirely in your browser.`,
      },
      {
        question: `Is my JSON data sent to a server?`,
        answer: `No. All formatting, validation, and parsing happens locally in your browser using JavaScript. Your JSON is never uploaded, logged, or stored on any server.`,
      },
      {
        question: `How big a JSON file can I format?`,
        answer: `In practice you can comfortably format files up to a few megabytes. The browser will handle anything that fits in memory, which for most APIs is well over 10 MB.`,
      },
      {
        question: `Does it work offline?`,
        answer: `Yes. Once the page has loaded, the formatter works fully offline because there is no server round-trip.`,
      },
      {
        question: `Can it also validate JSON?`,
        answer: `Yes. The Validate action checks the JSON without reformatting, and reports a clear error message with the line and column of the first syntax issue.`,
      },
      {
        question: `Does sorting keys change my data?`,
        answer: `Sorting is optional and off by default. When enabled, object keys are sorted alphabetically at every depth — the values and structure stay the same, only the key order changes.`,
      },
    ],
    relatedTools: [`json-validator`, `json-minifier`, `base64`, `jwt-decoder`],
    popular: true,
    status: `active`,
  },
  {
    id: `json-validator`,
    slug: `json-validator`,
    name: `JSON Validator`,
    category: `json`,
    shortDescription: `Validate JSON syntax and pinpoint the exact line and column of any error.`,
    longDescription: `The JSON Validator checks that a string is valid JSON and, when it is not, tells you exactly where the first problem is. Most online validators just say "invalid JSON" and leave you hunting through a wall of text — DevKit gives you the line number, column number, and the actual offending snippet so you can fix it in seconds. It is useful when you are writing JSON by hand, copy-pasting from somewhere that mangled the encoding, or troubleshooting a payload that an API keeps rejecting with a generic parse error. The validator runs entirely in your browser, so it is safe to paste production data, tokens, or anything sensitive — nothing is uploaded. You can paste the JSON, get a green check mark if it parses, or a red error block with the failing token and position. For convenience, the validator also gives you the same one-click copy and download as the formatter. If you also need to clean up indentation, switch to the JSON Formatter.`,
    keywords: [`json validator`, `json checker`, `validate json`, `json syntax checker`, `json error`],
    seo: {
      title: `JSON Validator Online — Validate JSON Instantly | DevKit`,
      description: `Validate JSON syntax and instantly locate errors by line and column. Free, fast, private — runs in your browser, no data uploaded.`,
      keywords: [`json validator`, `json checker`, `validate json`, `json syntax checker`, `json error`, `json lint`],
    },
    features: [
      `Clear error message with line and column of the first failure`,
      `100% client-side — your JSON never leaves the browser`,
      `Works on very large payloads (browser memory is the only limit)`,
      `Same Copy and Download actions as the formatter`,
      `Side-by-side with the Formatter for a complete JSON workflow`,
    ],
    howToSteps: [
      { name: `Paste your JSON`, text: `Drop the JSON you want to validate into the input area.` },
      { name: `Click Validate`, text: `Press the Validate button to check the syntax.` },
      { name: `Read the result`, text: `A green check confirms the JSON is valid. A red block shows the line and column of the first syntax error.` },
      { name: `Fix and re-check`, text: `Edit your JSON and validate again. For a quick cleanup, switch to the JSON Formatter.` },
    ],
    examples: [
      {
        title: `Missing comma between fields`,
        description: `Validator catches a missing comma and reports the position.`,
        input: `{\n  "name": "DevKit"\n  "version": "1.0"\n}`,
        output: `Invalid JSON — line 3, column 3: Expected ',' or '}' after property value.`,
      },
      {
        title: `Trailing comma (classic JS slip)`,
        description: `Strict JSON does not allow trailing commas.`,
        input: `[\n  "a",\n  "b",\n]`,
        output: `Invalid JSON — line 4, column 1: Unexpected token ] in JSON at position 16.`,
      },
      {
        title: `Valid input`,
        description: `A well-formed object passes validation.`,
        input: `{"name":"DevKit","runs":12832}`,
        output: `Valid JSON ✅`,
      },
    ],
    faqs: [
      {
        question: `How does the JSON Validator differ from the JSON Formatter?`,
        answer: `The Validator only checks whether the input is syntactically valid JSON and reports errors. The Formatter also re-indents and pretty-prints the output. Use the Validator when you only want a yes/no answer plus the error location.`,
      },
      {
        question: `Will it validate JSON5 or JavaScript object literals?`,
        answer: `No — the validator strictly follows the JSON specification (RFC 8259). Trailing commas, comments, and unquoted keys are not allowed. Use a JSON5-aware tool if you need those.`,
      },
      {
        question: `Is my JSON stored or logged?`,
        answer: `No. Validation runs entirely in your browser. DevKit never uploads, stores, or logs your input.`,
      },
      {
        question: `Why does my JSON fail with a strange character?`,
        answer: `The most common causes are smart quotes (’ ” instead of "), non-breaking spaces, or trailing commas copied from JavaScript. Replace them with plain ASCII and try again.`,
      },
      {
        question: `Can it validate huge files?`,
        answer: `Yes — anything that fits in your browser memory will parse. For files larger than a few megabytes, prefer a streaming JSON parser.`,
      },
    ],
    relatedTools: [`json-formatter`, `json-minifier`, `base64`, `jwt-decoder`],
    status: `active`,
  },
  {
    id: `json-minifier`,
    slug: `json-minifier`,
    name: `JSON Minifier`,
    category: `json`,
    shortDescription: `Compress JSON by stripping whitespace to shrink payloads.`,
    longDescription: `The JSON Minifier takes a formatted or whitespace-heavy JSON string and produces a single-line, no-whitespace version that is safe to ship. Every space, tab, and newline that is not required by the JSON grammar is removed, which typically cuts payload size by 15–30% depending on how much indentation the original had. This is the same transformation production build pipelines and CDNs apply when they gzip-friendly JSON — except DevKit does it on the fly in your browser, so you can copy a payload straight into a curl command, an environment variable, or a test fixture. The Minifier also validates as it goes: if your input is not valid JSON, the minifier will surface the same line/column error as the Validator so you can fix it first. There are no options to misconfigure — the output is always maximally compact JSON. Use it alongside the Formatter when you want to debug a small payload in DevTools and then ship it.`,
    keywords: [`json minifier`, `minify json`, `compress json`, `json compressor`],
    seo: {
      title: `JSON Minifier — Compress JSON Online | DevKit`,
      description: `Minify and compress JSON by removing unnecessary whitespace. Free, fast, private — optimize JSON for production and APIs.`,
      keywords: [`json minifier`, `minify json`, `compress json`, `json compressor`, `json optimizer`],
    },
    features: [
      `Removes every unneeded space, tab, and newline`,
      `Validates as it minifies — never produces invalid output`,
      `100% client-side — payload never leaves your browser`,
      `Same Copy and Download actions as the formatter`,
      `Often 15–30% smaller than pretty-printed JSON`,
    ],
    howToSteps: [
      { name: `Paste JSON`, text: `Drop the formatted JSON you want to compress into the input area.` },
      { name: `Click Minify`, text: `Press the Minify button.` },
      { name: `Inspect the size drop`, text: `Compare the input and output — the right side is the compact, single-line version.` },
      { name: `Copy or download`, text: `Copy the result or download it as a .json file.` },
    ],
    examples: [
      {
        title: `Pretty-printed config → compact`,
        description: `Same data, smaller bytes.`,
        input: `{\n  "name": "DevKit",\n  "version": "1.0.0",\n  "features": [\n    "json",\n    "jwt",\n    "base64"\n  ]\n}`,
        output: `{"name":"DevKit","version":"1.0.0","features":["json","jwt","base64"]}`,
      },
      {
        title: `Invalid JSON is rejected`,
        description: `Minifier refuses to produce output from broken JSON.`,
        input: `{ "name": "DevKit", }`,
        output: `Invalid JSON — line 1, column 21: Unexpected token } in JSON at position 20.`,
      },
    ],
    faqs: [
      {
        question: `How much smaller does the minified JSON get?`,
        answer: `It depends on indentation. JSON formatted with 2-space indentation typically shrinks by 20–30%. The savings stack with gzip or brotli compression on top.`,
      },
      {
        question: `Is the minified JSON still valid?`,
        answer: `Yes. The Minifier only removes whitespace. Keys, values, ordering, and types are preserved exactly.`,
      },
      {
        question: `Can I minify JSON with comments?`,
        answer: `No. Standard JSON does not allow comments, so commented input will fail validation. Strip the comments first.`,
      },
      {
        question: `Does it support JSON5 / trailing commas?`,
        answer: `No — strict JSON only (RFC 8259).`,
      },
    ],
    relatedTools: [`json-formatter`, `json-validator`, `base64`, `regex-tester`],
    status: `active`,
  },

  /* --------------------------- Encoding --------------------------- */
  {
    id: `base64`,
    slug: `base64`,
    name: `Base64 Encoder / Decoder`,
    category: `encoding`,
    shortDescription: `Encode text to Base64 or decode Base64 strings back to readable text.`,
    longDescription: `The Base64 Encoder / Decoder converts plain text into the Base64 representation (and back) entirely in your browser. Base64 is the encoding you meet everywhere in software: HTTP basic auth headers, JWT tokens, data URLs in CSS, MIME attachments, and a thousand configuration payloads. It is not encryption, it is a transport encoding — anyone with the string can decode it. DevKit handles the two things you usually trip over: Unicode (emoji, CJK, accented characters survive a round trip) and padding (you can paste strings with or without the trailing = characters and DevKit will fix it). The encoder also exposes a URL-safe mode (replacing + and / with - and _) which is what JWT and most URL contexts need. Paste a string, choose the mode, and you get the result with one-tap actions to copy or download. Because everything runs locally, you can safely encode and decode tokens, secrets, or payloads you would not want to upload to a random web tool.`,
    keywords: [`base64 encoder`, `base64 decoder`, `encode base64`, `decode base64`, `base64 converter`],
    seo: {
      title: `Base64 Encoder / Decoder — Free Online Tool | DevKit`,
      description: `Encode text to Base64 or decode Base64 to text. Free, fast, private — runs in your browser. Supports URL-safe mode and Unicode.`,
      keywords: [`base64 encoder`, `base64 decoder`, `encode base64`, `decode base64`, `base64 converter`, `base64 online`],
    },
    features: [
      `Encode and decode in one tool — no separate pages`,
      `Unicode-safe round trip (emoji, accents, CJK)`,
      `URL-safe mode (-_ instead of +/) for JWTs`,
      `Auto-fixes missing or extra Base64 padding`,
      `One-click copy and download`,
    ],
    howToSteps: [
      { name: `Pick a mode`, text: `Choose Encode (text → Base64) or Decode (Base64 → text).` },
      { name: `Paste input`, text: `Drop your text or Base64 string into the input area.` },
      { name: `Convert`, text: `Click Convert (or press the matching action) to produce the result.` },
      { name: `Copy or download`, text: `Use Copy or Download to take the result with you.` },
    ],
    examples: [
      {
        title: `Encode plain text`,
        description: `ASCII text encodes predictably.`,
        input: `Hello World`,
        output: `SGVsbG8gV29ybGQ=`,
      },
      {
        title: `Encode Unicode`,
        description: `Emoji and accented characters survive a round trip.`,
        input: `DevKit 🚀`,
        output: `RGV2S2l0IOKAmFA=`,
      },
      {
        title: `Decode Base64`,
        description: `Reverse the encoding to recover the original text.`,
        input: `SGVsbG8gV29ybGQ=`,
        output: `Hello World`,
      },
    ],
    faqs: [
      {
        question: `Is Base64 encryption?`,
        answer: `No. Base64 is an encoding, not encryption. Anyone with the encoded string can decode it in one click. Never use Base64 to hide secrets.`,
      },
      {
        question: `Why is my encoded string different from another tool?`,
        answer: `Different tools use different Base64 variants. Standard Base64 uses + and / and = padding. URL-safe Base64 uses - and _ with optional padding. JWTs use URL-safe Base64.`,
      },
      {
        question: `Is my text uploaded anywhere?`,
        answer: `No. Conversion happens entirely in your browser. DevKit does not log, store, or transmit your input.`,
      },
      {
        question: `Can I encode files?`,
        answer: `This tool works with text input. For binary files (images, PDFs), use a desktop tool or your language of choice — Base64 over a browser file picker is coming in a future DevKit release.`,
      },
      {
        question: `Why do I sometimes see line breaks in Base64?`,
        answer: `Some tools wrap Base64 at 76 characters per line for MIME compatibility. DevKit produces single-line output by default, which is what most modern APIs want.`,
      },
    ],
    relatedTools: [`url-encoder`, `jwt-decoder`, `json-formatter`],
    popular: true,
    status: `active`,
  },
  {
    id: `url-encoder`,
    slug: `url-encoder`,
    name: `URL Encoder / Decoder`,
    category: `encoding`,
    shortDescription: `Percent-encode URLs for safe transmission or decode them back to text.`,
    longDescription: `The URL Encoder / Decoder applies percent-encoding (RFC 3986) to a string, or reverses it. Percent-encoding is what makes URLs safe to ship — spaces become %20, slashes inside query parameters become %2F, ampersands that are part of a value become %26, and so on. Most APIs require query parameters to be percent-encoded before you concatenate them into a URL. DevKit runs entirely in your browser, so you can safely encode or decode URLs that contain tokens, API keys, or session IDs. It supports both encodeURIComponent (used for individual query parameter values) and encodeURI (used for whole URLs) — the difference matters: encodeURIComponent leaves "structural" characters like /, ?, &, and # intact, while encodeURIComponent escapes them too. If you are unsure which to use, start with a self-contained single value and decodeURIComponent for a single value, since that is what 95% of code uses.`,
    keywords: [`url encoder`, `url encode`, `percent encoding`, `url decoder`, `encode url`],
    seo: {
      title: `URL Encoder / Decoder — Percent-encoding Online | DevKit`,
      description: `Encode URLs for safe transmission or decode URL-encoded strings. Free, fast, private — runs in your browser. Supports both encodeURI and encodeURIComponent.`,
      keywords: [`url encoder`, `url encode`, `percent encoding`, `url decoder`, `encode url`, `decode url`],
    },
    features: [
      `Encode and decode in one tool`,
      `Handles Unicode (emoji, accents, CJK) without breaking the URL`,
      `Optional encodeURIComponent vs encodeURI modes`,
      `100% client-side — never uploads your URLs`,
      `One-click copy and download`,
    ],
    howToSteps: [
      { name: `Choose a mode`, text: `Pick Encode (text → percent-encoded) or Decode (percent-encoded → text).` },
      { name: `Paste input`, text: `Drop the URL fragment or query value into the input area.` },
      { name: `Convert`, text: `Press the Encode or Decode action.` },
      { name: `Copy or download`, text: `Copy the result or download it as a .txt file.` },
    ],
    examples: [
      {
        title: `Encode a query value`,
        description: `A value containing spaces and ampersands gets safely percent-encoded.`,
        input: `name=John Doe&note=Hello & welcome`,
        output: `name%3DJohn%20Doe%26note%3DHello%20%26%20welcome`,
      },
      {
        title: `Decode a percent-encoded string`,
        description: `Reverse the encoding to recover the original text.`,
        input: `https%3A%2F%2Fexample.com%2Fpath%3Fa%3D1%26b%3Dtwo`,
        output: `https://example.com/path?a=1&b=two`,
      },
    ],
    faqs: [
      {
        question: `What is the difference between encodeURI and encodeURIComponent?`,
        answer: `encodeURIComponent escapes everything except letters, digits, and - _ . ~ !. encodeURI also leaves URL structural characters like /, ?, &, and # intact. Use encodeURIComponent for individual query values; use encodeURI for whole URLs.`,
      },
      {
        question: `Why does my encoded URL not work?`,
        answer: `Most often because you double-encoded (encoded twice, or encoded the whole URL after a library already encoded the parts). Try decoding once and check whether the result is still percent-encoded.`,
      },
      {
        question: `Is my URL stored or shared?`,
        answer: `No. All encoding happens in your browser. DevKit never uploads or logs your input.`,
      },
    ],
    relatedTools: [`base64`, `jwt-decoder`, `json-formatter`],
    status: `active`,
  },

  /* --------------------------- Security --------------------------- */
  {
    id: `jwt-decoder`,
    slug: `jwt-decoder`,
    name: `JWT Decoder`,
    category: `security`,
    shortDescription: `Decode and inspect JWT tokens — header, payload, signature, and claim timestamps.`,
    longDescription: `The JWT Decoder takes a JSON Web Token and breaks it into its three parts: the header (algorithm and type), the payload (the claims — who, what, when, and any custom fields), and the signature (the cryptographic seal). It does NOT verify the signature — verifying a JWT requires the issuer's signing key, and pasting a production token into a third-party verifier is exactly how you leak them. This tool is for inspection only: read the claims, check the expiration, and sanity-check the structure. DevKit runs entirely in your browser, so the token never leaves your device. The decoder auto-formats timestamps (iat, nbf, exp) into human-readable dates, flags expired tokens, and surfaces common claims (sub, iss, aud, jti) with the rest of the payload. It also recognizes and pretty-prints nested JSON inside the payload. If you are debugging an authentication flow or triaging a "why is this request 401" ticket, this is the fastest way to see what your token actually says.`,
    keywords: [`jwt decoder`, `jwt debugger`, `decode jwt`, `jwt token decoder`, `jwt parser`, `json web token`],
    seo: {
      title: `JWT Decoder — Decode JWT Tokens Online | DevKit`,
      description: `Decode and inspect JWT tokens instantly. View header, payload, signature, and claim timestamps. Free, fast, private — runs in your browser.`,
      keywords: [`jwt decoder`, `jwt debugger`, `decode jwt`, `jwt token decoder`, `jwt parser`, `json web token`, `jwt.io alternative`],
    },
    disclaimer: `Decoding a JWT does not verify its signature. This tool is for inspection only. Never paste production secrets, signing keys, or tokens with sensitive claims into any online tool — including this one.`,
    features: [
      `Decodes header, payload, and signature in one view`,
      `Auto-formats iat, nbf, exp timestamps into readable dates`,
      `Flags expired tokens at a glance`,
      `Handles JWTs with or without padding`,
      `100% client-side — your token never leaves your browser`,
    ],
    howToSteps: [
      { name: `Paste the JWT`, text: `Drop the token into the input area — header.payload.signature, in that order.` },
      { name: `Inspect the parts`, text: `Read the header and payload in their parsed JSON form. The signature is shown as-is.` },
      { name: `Check expiration`, text: `If the payload contains exp, the decoder will flag expired tokens.` },
      { name: `Copy or download`, text: `Copy the parsed payload or download it as JSON.` },
    ],
    examples: [
      {
        title: `Classic example token`,
        description: `The canonical JWT.io demo token, decoded.`,
        input: `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c`,
        output: `Header: { "alg": "HS256", "typ": "JWT" }\nPayload: { "sub": "1234567890", "name": "John Doe", "iat": 1516239022 }`,
      },
      {
        title: `Malformed token`,
        description: `Tokens that do not have three dot-separated parts are rejected.`,
        input: `this.is.not.a.jwt`,
        output: `Invalid JWT — expected 3 segments separated by dots.`,
      },
    ],
    faqs: [
      {
        question: `Does the JWT Decoder verify the signature?`,
        answer: `No. Signature verification requires the issuer's signing key and is intentionally not implemented in any browser-based decoder. This tool only decodes and inspects.`,
      },
      {
        question: `Is it safe to paste a real token?`,
        answer: `The token is processed entirely in your browser — DevKit never uploads it. However, treat every token as if it were a password. If you would not share it on a public channel, do not paste it into any tool — including this one — on a machine you do not trust.`,
      },
      {
        question: `Why does the decoder reject my token?`,
        answer: `The most common causes are: it is not a JWT (only 2 or 4 parts), the Base64URL padding was corrupted, or one of the segments is not valid Base64URL.`,
      },
      {
        question: `What is the difference between iat, nbf, and exp?`,
        answer: `iat is "issued at", nbf is "not before", and exp is "expiration". They are all Unix timestamps in seconds. The decoder converts them to readable dates.`,
      },
    ],
    relatedTools: [`base64`, `uuid-generator`, `json-formatter`],
    popular: true,
    status: `active`,
  },

  /* ----------------------------- Regex ---------------------------- */
  {
    id: `regex-tester`,
    slug: `regex-tester`,
    name: `Regex Tester`,
    category: `regex`,
    shortDescription: `Test and debug regular expressions in real time with match highlighting.`,
    longDescription: `The Regex Tester lets you write a regular expression, paste a string to test it against, and instantly see which parts match. Every match is highlighted inline, and a side panel lists each match with its capture groups so you can tell at a glance whether your pattern is doing what you think it is. DevKit supports JavaScript-flavored regex, including the global (g), case-insensitive (i), multiline (m), dotAll (s), and Unicode (u) flags. The Tester is also a replacement tool: write a pattern and a replacement, and DevKit shows the result of applying that replacement across the whole test string. This is invaluable when you are building a regex you will eventually ship in code and you want to be sure it behaves on the messy input you will actually see in production. Because the engine runs in your browser, you can paste sensitive log lines or payload data without it ever being uploaded.`,
    keywords: [`regex tester`, `regex tester online`, `regular expression tester`, `regex debugger`, `test regex`],
    seo: {
      title: `Regex Tester — Test Regular Expressions Online | DevKit`,
      description: `Test and debug regular expressions in real time. Match highlighting, capture groups, and live replacement. Free, fast, private.`,
      keywords: [`regex tester`, `regex tester online`, `regular expression tester`, `regex debugger`, `test regex`, `regex playground`],
    },
    features: [
      `Real-time match highlighting on the test string`,
      `Side panel listing every match with capture groups`,
      `Live replacement preview — see the result as you type`,
      `Toggle g, i, m, s, and u flags`,
      `100% client-side — test data never leaves your browser`,
    ],
    howToSteps: [
      { name: `Write a pattern`, text: `Type your regular expression in the pattern field. Choose the flags you want.` },
      { name: `Paste a test string`, text: `Drop the text you want to test against in the test area.` },
      { name: `Read matches`, text: `Matches are highlighted inline and listed below with their capture groups.` },
      { name: `Try a replacement`, text: `Optionally type a replacement string to see the substituted output.` },
    ],
    examples: [
      {
        title: `Match capitalized words`,
        description: `A simple pattern that finds words starting with a capital letter.`,
        input: `Hello World, John Doe, Jane Smith`,
        output: `Matches: Hello, World, John, Doe, Jane, Smith`,
      },
      {
        title: `Capture email local-part and domain`,
        description: `Group parts of an email address with parentheses.`,
        input: `support@example.com and hello@example.com`,
        output: `Match: support@example.com — group 1: support, group 2: example.com\nMatch: hello@example.com — group 1: hello, group 2: example.com`,
      },
    ],
    faqs: [
      {
        question: `Which regex flavor does this use?`,
        answer: `DevKit uses JavaScript's built-in RegExp engine. That means ECMAScript syntax (lookbehind, named groups, Unicode property escapes), but not PCRE-only features like possessive quantifiers.`,
      },
      {
        question: `Why is my match "sticky"?`,
        answer: `The global flag (g) is on by default. Without g, only the first match is reported. Toggle flags as needed.`,
      },
      {
        question: `Is my test string uploaded?`,
        answer: `No. The regex engine runs entirely in your browser. DevKit never uploads or logs your input.`,
      },
    ],
    relatedTools: [`json-formatter`, `text-diff`, `base64`],
    popular: true,
    status: `active`,
  },

  /* ----------------------------- Time ----------------------------- */
  {
    id: `timestamp-converter`,
    slug: `timestamp-converter`,
    name: `Unix Timestamp Converter`,
    category: `time`,
    shortDescription: `Convert between Unix timestamps and human-readable dates in seconds or milliseconds.`,
    longDescription: `The Unix Timestamp Converter goes both ways: paste a Unix timestamp (in seconds or milliseconds) and get a full human-readable breakdown — UTC date, ISO 8601 string, your local time, and a friendly relative phrase like "2 hours ago". Or paste a date in any common format and get the equivalent Unix timestamp in seconds and milliseconds. It also surfaces the current timestamp with a single click, which is the fastest way to grab "now" for a database column, a cache key, or a log entry. DevKit runs entirely in your browser, so you can convert timestamps from logs that contain sensitive data without uploading them. The converter supports timezones: see what the same instant looks like in UTC, your local zone, or any of the common zones developers care about. It is the utility you reach for when you are staring at a raw epoch in a database and need to figure out when something actually happened.`,
    keywords: [`unix timestamp converter`, `timestamp converter`, `epoch converter`, `unix time`, `unix timestamp`],
    seo: {
      title: `Unix Timestamp Converter — Convert Time Online | DevKit`,
      description: `Convert Unix timestamps to dates and vice versa. Support for seconds, milliseconds, ISO 8601, and timezones. Free, fast, private.`,
      keywords: [`unix timestamp converter`, `timestamp converter`, `epoch converter`, `unix time`, `unix timestamp`, `epoch to date`],
    },
    features: [
      `Bidirectional conversion — timestamp ↔ date`,
      `Seconds and milliseconds`,
      `UTC, local, and common timezone display`,
      `Live "current timestamp" button`,
      `ISO 8601 output and friendly relative phrasing`,
      `100% client-side — never uploads your data`,
    ],
    howToSteps: [
      { name: `Pick a unit`, text: `Choose seconds or milliseconds for the input.` },
      { name: `Paste a timestamp or date`, text: `Paste a Unix timestamp or a date string into the input area.` },
      { name: `Convert`, text: `Click Convert. The other side fills in.` },
      { name: `Copy`, text: `Copy the result to your clipboard or download it as JSON.` },
    ],
    examples: [
      {
        title: `Timestamp → date`,
        description: `A common epoch converted to UTC, local, and ISO 8601.`,
        input: `1704067200`,
        output: `UTC: 2024-01-01 00:00:00 UTC\nISO 8601: 2024-01-01T00:00:00Z\nRelative: 1 year ago`,
      },
      {
        title: `Date → timestamp`,
        description: `A date string converted to Unix seconds and milliseconds.`,
        input: `2024-01-01`,
        output: `1704067200 (seconds)\n1704067200000 (milliseconds)`,
      },
    ],
    faqs: [
      {
        question: `Should I use seconds or milliseconds?`,
        answer: `It depends on the language. Unix, Linux, and most backend code use seconds. JavaScript, Java (System.currentTimeMillis), and some databases use milliseconds. Check the system you are converting for.`,
      },
      {
        question: `What timezone is the output in?`,
        answer: `The output includes UTC, your local time, and any zone you pick from the dropdown. ISO 8601 always includes the Z / offset so it is unambiguous.`,
      },
      {
        question: `Why does my timestamp look like it is in the future?`,
        answer: `You probably used milliseconds where seconds were expected (or vice versa). Multiplied/divide by 1000 and try again.`,
      },
      {
        question: `Is my data uploaded?`,
        answer: `No. The conversion runs entirely in your browser.`,
      },
    ],
    relatedTools: [`uuid-generator`, `jwt-decoder`, `json-formatter`],
    status: `active`,
  },

  /* ----------------------------- Text ----------------------------- */
  {
    id: `text-diff`,
    slug: `text-diff`,
    name: `Text Diff Checker`,
    category: `text`,
    shortDescription: `Compare two texts side by side and highlight every addition, removal, and unchanged line.`,
    longDescription: `The Text Diff Checker lines up two pieces of text and highlights what changed. Paste the "before" and "after" versions, choose a view mode (side-by-side or inline), and DevKit produces a line-by-line diff with additions in green and removals in red. This is the tool you reach for when a config file has drifted from the last commit, a generated file is no longer matching the template, or you need to review changes between two versions of a contract, log, or any other plain text. The checker supports whitespace and case sensitivity toggles — useful when the only difference is indentation or capitalization that you do not actually care about. The diff runs entirely in your browser, so you can compare documents you would not want to upload to a third-party diff service. For code-style diffing, the output is unstyled plain text by default; for prose-style diffing, you can switch to inline mode which produces a single highlighted document.`,
    keywords: [`diff checker`, `text diff`, `compare text`, `text comparison`, `text differ`],
    seo: {
      title: `Text Diff Checker — Compare Text Online | DevKit`,
      description: `Compare two texts and highlight differences with our free online diff checker. Side-by-side and inline views. Whitespace and case toggles.`,
      keywords: [`diff checker`, `text diff`, `compare text`, `text comparison`, `text differ`, `online diff`],
    },
    features: [
      `Side-by-side or unified inline view`,
      `Whitespace and case-insensitive toggles`,
      `Line-level additions, removals, and unchanged counts`,
      `100% client-side — your text never leaves the browser`,
      `Copy or download the diff as plain text`,
    ],
    howToSteps: [
      { name: `Paste original`, text: `Drop the "before" text into the Original area.` },
      { name: `Paste modified`, text: `Drop the "after" text into the Modified area.` },
      { name: `Compare`, text: `Press Compare to see the diff. Switch to inline view if you prefer a single document.` },
      { name: `Copy or download`, text: `Copy the diff or download it as a .txt or .diff file.` },
    ],
    examples: [
      {
        title: `Two config versions`,
        description: `A timeout change and an extra option show up as one removed line and one added line.`,
        input: `Original:\napi.timeout=3000\napi.retries=3\n\nModified:\napi.timeout=5000\napi.retries=3\napi.baseUrl=https://api.example.com`,
        output: `Removed: api.timeout=3000\nAdded:   api.timeout=5000\nAdded:   api.baseUrl=https://api.example.com`,
      },
    ],
    faqs: [
      {
        question: `Does it work on code?`,
        answer: `Yes — anything that is plain text. For syntax-highlighted code diffs, paste the original and modified into separate files in your editor; this tool focuses on the line-level difference, which is what most config and data comparisons need.`,
      },
      {
        question: `What does "ignore whitespace" do?`,
        answer: `It strips leading and trailing whitespace from each line before comparing, so lines that only differ in indentation or trailing spaces are treated as equal.`,
      },
      {
        question: `Can I diff large files?`,
        answer: `Yes — anything that fits in your browser memory will diff. For multi-megabyte files, a dedicated diff tool is faster.`,
      },
      {
        question: `Is my text uploaded?`,
        answer: `No. Diffing runs entirely in your browser.`,
      },
    ],
    relatedTools: [`json-formatter`, `base64`, `regex-tester`],
    status: `active`,
  },

  /* --------------------------- Utility ---------------------------- */
  {
    id: `uuid-generator`,
    slug: `uuid-generator`,
    name: `UUID Generator`,
    category: `utility`,
    shortDescription: `Generate UUID v4 (random) or UUID v7 (time-ordered) one at a time or in bulk.`,
    longDescription: `The UUID Generator creates Universally Unique Identifiers (UUIDs) entirely in your browser. Choose between UUID v4 — the random 128-bit value you have probably used for years — and UUID v7, the newer time-ordered variant that sorts naturally by creation time and works much better as a database primary key. You can generate one UUID or a hundred at once, and copy them with a single click. Every UUID is generated locally with crypto.getRandomValues, so the quality is cryptographically strong and there is no need to trust a remote service. UUID v4 is what you want for opaque IDs where the only requirement is uniqueness. UUID v7 is what you want when the IDs will land in a sorted index (most databases) — they avoid the page-cache thrashing you get with random UUIDs in B-tree indexes, and you also get a free creation-time sort. DevKit never logs the IDs it generates, so it is safe to use for any application.`,
    keywords: [`uuid generator`, `uuid v4`, `uuid v7`, `generate uuid`, `unique id`],
    seo: {
      title: `UUID Generator — UUID v4 and v7 Online | DevKit`,
      description: `Generate UUID v4 (random) and UUID v7 (time-ordered) online. Bulk generation, one-click copy. Free, fast, private — runs in your browser.`,
      keywords: [`uuid generator`, `uuid v4`, `uuid v7`, `generate uuid`, `unique id`, `guid generator`],
    },
    features: [
      `UUID v4 (random) and UUID v7 (time-ordered)`,
      `Bulk generation — up to 100 at a time`,
      `Cryptographically strong via crypto.getRandomValues`,
      `One-click copy for a single UUID or all of them`,
      `100% client-side — IDs never leave your browser`,
    ],
    howToSteps: [
      { name: `Pick a version`, text: `Choose v4 (random) or v7 (time-ordered).` },
      { name: `Pick a quantity`, text: `Choose how many UUIDs to generate — from 1 to 100.` },
      { name: `Generate`, text: `Click Generate.` },
      { name: `Copy`, text: `Copy the list to your clipboard or download it as a .txt file.` },
    ],
    examples: [
      {
        title: `A single UUID v4`,
        description: `The classic random UUID.`,
        input: `Generate 1 × v4`,
        output: `8d4f7a2b-9c11-4f63-b5e8-7e4a1f9d2c5b`,
      },
      {
        title: `Time-ordered UUID v7`,
        description: `The first 12 hex digits encode the millisecond timestamp.`,
        input: `Generate 1 × v7`,
        output: `018f3b9c-7d2a-7c9e-b8e7-2c5b9d4f7a2b`,
      },
    ],
    faqs: [
      {
        question: `Should I use v4 or v7?`,
        answer: `Use v4 for opaque IDs where randomness is the only requirement. Use v7 when the IDs land in a sorted index (most databases) — they avoid index fragmentation and sort by creation time for free.`,
      },
      {
        question: `Are the UUIDs cryptographically random?`,
        answer: `Yes. DevKit uses crypto.getRandomValues, the same source your browser uses for secure contexts.`,
      },
      {
        question: `Are UUIDs truly unique?`,
        answer: `For practical purposes, yes. A random 122-bit value has a collision probability below one in a trillion even after generating a billion UUIDs per second for a century.`,
      },
      {
        question: `Is anything logged?`,
        answer: `No. UUID generation runs entirely in your browser. DevKit does not store the IDs.`,
      },
    ],
    relatedTools: [`jwt-decoder`, `timestamp-converter`, `base64`],
    popular: true,
    status: `active`,
  },

  /* -------------------------- Phase 2: JSON Viewer + YAML ------------------ */
  {
    id: `json-viewer`,
    slug: `json-viewer`,
    name: `JSON Viewer`,
    category: `json`,
    shortDescription: `Visualize JSON as an interactive, color-coded tree you can expand, collapse, and search.`,
    longDescription: `The JSON Viewer takes any JSON document and renders it as a collapsible tree so you can actually see the structure. Each node is color-coded by type — objects in amber, arrays in purple, strings in green, numbers in blue, booleans in pink, and null in slate — so you can read a complex payload at a glance. Every node shows its key (or array index), its type, and a one-line preview; expanding an object or array reveals its children one level at a time. A search box lets you filter the tree by key, value, or path — useful when you are looking for one specific field inside a deeply nested response. The Viewer also reports the total node count and maximum depth so you know how deep the data goes. It runs entirely in your browser, so it is safe to paste production payloads, API responses, or any data you would not want to upload to a third-party formatter. For editing the structure, switch to the JSON Formatter; for converting to YAML or back, use the converters below.`,
    keywords: [`json viewer`, `json tree`, `json explorer`, `json navigator`, `view json`],
    seo: {
      title: `JSON Viewer — Interactive JSON Tree Viewer | DevKit`,
      description: `Visualize JSON as an interactive tree. Expand, collapse, search, and inspect any JSON document. Free, fast, private — runs in your browser.`,
      keywords: [`json viewer`, `json tree`, `json explorer`, `json navigator`, `view json`, `json structure`],
    },
    features: [
      `Color-coded tree by node type — objects, arrays, strings, numbers, booleans, null`,
      `Expand and collapse individual nodes or all at once`,
      `Live search by key, value, or path`,
      `Reports total node count and maximum depth`,
      `100% client-side — JSON never leaves your browser`,
    ],
    howToSteps: [
      { name: `Paste your JSON`, text: `Drop any JSON document into the input area.` },
      { name: `Build the tree`, text: `Click Build Tree to parse the JSON and render the structure.` },
      { name: `Navigate`, text: `Expand and collapse branches with the chevron icons. Search to filter by key, value, or path.` },
      { name: `Inspect`, text: `Each node shows its key, type, and preview value. Look at the node count and depth badge to gauge complexity.` },
    ],
    examples: [
      {
        title: `Inspect a package.json`,
        description: `See every dependency and script in a real-world config file.`,
        input: `{\n  "name": "devkit",\n  "scripts": {\n    "dev": "vite",\n    "build": "tsc && vite build"\n  },\n  "dependencies": {\n    "react": "^18.2.0",\n    "vite": "^5.0.0"\n  }\n}`,
        output: `Root (object, 3 keys): name, scripts, dependencies\n└─ scripts (object, 2 keys)\n   ├─ dev: "vite"\n   └─ build: "tsc && vite build"\n└─ dependencies (object, 2 keys)\n   ├─ react: "^18.2.0"\n   └─ vite: "^5.0.0"`,
      },
    ],
    faqs: [
      {
        question: `What is the difference between JSON Viewer and JSON Formatter?`,
        answer: `The Viewer renders the structure as a navigable tree, useful for understanding large or nested documents. The Formatter rewrites the JSON with consistent indentation for editing or pasting elsewhere. Use both — the Viewer to understand, the Formatter to edit.`,
      },
      {
        question: `How deep can the tree go?`,
        answer: `There is no hard limit. The Viewer handles arbitrarily nested JSON up to what fits in your browser's memory.`,
      },
      {
        question: `Can I edit values in the tree?`,
        answer: `Not in this version — the Viewer is read-only by design. Use the JSON Formatter to edit and the Viewer to verify the structure.`,
      },
      {
        question: `Is my JSON uploaded?`,
        answer: `No. Parsing and rendering happen entirely in your browser.`,
      },
    ],
    relatedTools: [`json-formatter`, `json-validator`, `json-to-yaml`, `yaml-to-json`],
    status: `active`,
  },
  {
    id: `json-to-yaml`,
    slug: `json-to-yaml`,
    name: `JSON to YAML Converter`,
    category: `json`,
    shortDescription: `Convert JSON data into clean, indented YAML in your browser.`,
    longDescription: `The JSON to YAML Converter translates a JSON document into its YAML equivalent, preserving every key, array, and value exactly. YAML is widely used for configuration files (Kubernetes manifests, GitHub Actions workflows, Ansible playbooks, Docker Compose, Cloudflare config) because it is more human-readable than JSON — no braces, no quotes around strings, no commas between items. DevKit lets you pick 2-space or 4-space indentation to match your project's style, and the output preserves key order from the input. The converter validates your JSON first, so an invalid input never produces a misleading YAML file — it surfaces the same line-and-column error as the JSON Validator. Conversion runs entirely in your browser using js-yaml, so your data never leaves your device. For the reverse direction, use the YAML to JSON Converter.`,
    keywords: [`json to yaml`, `convert json to yaml`, `json yaml converter`, `yaml from json`],
    seo: {
      title: `JSON to YAML Converter — Online | DevKit`,
      description: `Convert JSON to YAML online. Preserves structure and key order, supports 2 or 4 space indent. Free, fast, private — runs in your browser.`,
      keywords: [`json to yaml`, `convert json to yaml`, `json yaml converter`, `yaml from json`, `json2yaml`],
    },
    features: [
      `Preserves key order and all data types from the input`,
      `2-space or 4-space indentation to match project style`,
      `Validates JSON before converting — surfaces errors clearly`,
      `100% client-side — your JSON never leaves your browser`,
      `One-click copy and download as .yaml`,
    ],
    howToSteps: [
      { name: `Paste your JSON`, text: `Drop the JSON document into the input area.` },
      { name: `Pick indentation`, text: `Choose 2 or 4 spaces for the YAML output.` },
      { name: `Convert`, text: `Click Convert to YAML.` },
      { name: `Copy or download`, text: `Copy the YAML to your clipboard or download it as a .yaml file.` },
    ],
    examples: [
      {
        title: `package.json → YAML`,
        description: `Same data, more readable for config files.`,
        input: `{\n  "name": "devkit",\n  "version": "1.0.0",\n  "scripts": {\n    "dev": "vite",\n    "build": "tsc && vite build"\n  }\n}`,
        output: `name: devkit\nversion: 1.0.0\nscripts:\n  dev: vite\n  build: tsc && vite build\n`,
      },
    ],
    faqs: [
      {
        question: `Are there JSON values that do not round-trip to YAML?`,
        answer: `Almost everything round-trips cleanly. The main gotcha is YAML treats unquoted yes/no/on/off as booleans — DevKit quotes them in the output so the JSON string "yes" stays a string when read back.`,
      },
      {
        question: `Does it preserve key order?`,
        answer: `Yes — keys appear in the YAML in the same order as in your JSON input.`,
      },
      {
        question: `Can I convert huge JSON files?`,
        answer: `Yes — anything that fits in browser memory will convert. For multi-megabyte files, prefer a streaming tool.`,
      },
    ],
    relatedTools: [`json-formatter`, `yaml-to-json`, `json-viewer`],
    status: `active`,
  },
  {
    id: `yaml-to-json`,
    slug: `yaml-to-json`,
    name: `YAML to JSON Converter`,
    category: `json`,
    shortDescription: `Convert YAML into clean, indented JSON in your browser.`,
    longDescription: `The YAML to JSON Converter translates a YAML document into its JSON representation, so you can use a YAML config file in a JSON-only tool, ship a YAML workflow as JSON for an API, or normalize mixed-format data. The converter understands standard YAML — mappings, sequences, scalars, multi-line strings, and common number/boolean/null formats. Pick 2-space or 4-space indentation to match the JSON style of your project, then copy or download the result. DevKit surfaces clear errors when the YAML is malformed, with the offending line so you can fix the source. Conversion runs entirely in your browser using js-yaml, so your config never leaves your device. For the reverse direction, use the JSON to YAML Converter.`,
    keywords: [`yaml to json`, `convert yaml to json`, `yaml json converter`, `json from yaml`],
    seo: {
      title: `YAML to JSON Converter — Online | DevKit`,
      description: `Convert YAML to JSON online. Preserves structure, supports 2 or 4 space indent. Free, fast, private — runs in your browser.`,
      keywords: [`yaml to json`, `convert yaml to json`, `yaml json converter`, `json from yaml`, `yaml2json`],
    },
    features: [
      `Parses standard YAML — mappings, sequences, scalars, multi-line strings`,
      `2-space or 4-space indentation for the JSON output`,
      `Clear error messages on invalid YAML`,
      `100% client-side — your YAML never leaves your browser`,
      `One-click copy and download as .json`,
    ],
    howToSteps: [
      { name: `Paste your YAML`, text: `Drop the YAML document into the input area.` },
      { name: `Pick indentation`, text: `Choose 2 or 4 spaces for the JSON output.` },
      { name: `Convert`, text: `Click Convert to JSON.` },
      { name: `Copy or download`, text: `Copy the JSON to your clipboard or download it as a .json file.` },
    ],
    examples: [
      {
        title: `GitHub Actions workflow`,
        description: `A typical YAML workflow becomes JSON for an API or tool.`,
        input: `name: CI\non:\n  push:\n    branches: [main]\njobs:\n  build:\n    runs-on: ubuntu-latest\n    steps:\n      - uses: actions/checkout@v4\n`,
        output: `{\n  "name": "CI",\n  "on": {\n    "push": {\n      "branches": [\n        "main"\n      ]\n    }\n  },\n  "jobs": {\n    "build": {\n      "runs-on": "ubuntu-latest",\n      "steps": [\n        {\n          "uses": "actions/checkout@v4"\n        }\n      ]\n    }\n  }\n}`,
      },
    ],
    faqs: [
      {
        question: `Does it support YAML anchors and aliases?`,
        answer: `Yes — js-yaml fully resolves anchors (&) and aliases (*) during conversion, so the output JSON has the resolved values.`,
      },
      {
        question: `What happens to YAML comments?`,
        answer: `Comments are stripped, since JSON has no comment syntax. The data is preserved exactly.`,
      },
      {
        question: `Are there YAML constructs that cannot be converted?`,
        answer: `Only constructs that JSON cannot represent — YAML sets and custom tags. DevKit serializes them as objects so the data still makes it across.`,
      },
    ],
    relatedTools: [`json-formatter`, `json-to-yaml`, `json-viewer`],
    status: `active`,
  },

  /* ------------------ Phase 2: HTML/Binary/Hex Encoders ------------------- */
  {
    id: `html-entity-encoder`,
    slug: `html-entity-encoder`,
    name: `HTML Entity Encoder / Decoder`,
    category: `encoding`,
    shortDescription: `Encode special characters as HTML entities or decode entities back to text.`,
    longDescription: `The HTML Entity Encoder / Decoder escapes the five characters that are special in HTML — <, >, &, ", and ' — into named or numeric entities (&lt; &gt; &amp; &quot; &#39;) so the text can be safely embedded in an HTML document or attribute. It also decodes in the opposite direction, recognizing the most common named and numeric entities including &nbsp;, &apos;, &#x27;, and hex forms. The encoder is the simplest defense against HTML injection: any user-supplied string that you intend to render should be entity-encoded first. DevKit also runs the reverse — useful when you have copy-pasted HTML with entities and need the raw text for a config file or a markdown document. All conversion runs locally in your browser, so you can safely process user-generated content or markup you are not sure you trust.`,
    keywords: [`html entity encoder`, `html entity decoder`, `html escape`, `html unescape`, `html entities`],
    seo: {
      title: `HTML Entity Encoder / Decoder — Free Online Tool | DevKit`,
      description: `Encode HTML special characters to entities (&lt; &gt; &amp;) or decode entities back. Free, fast, private — runs in your browser.`,
      keywords: [`html entity encoder`, `html entity decoder`, `html escape`, `html unescape`, `html entities`, `xss prevention`],
    },
    features: [
      `Encode and decode in one tool`,
      `Handles <, >, &, ", and ' correctly`,
      `Decodes named entities (&amp;, &lt;, &gt;, &quot;, &apos;, &nbsp;) and numeric/hex entities`,
      `100% client-side — never uploads your text`,
      `One-click copy`,
    ],
    howToSteps: [
      { name: `Choose a mode`, text: `Pick Encode (text → entities) or Decode (entities → text).` },
      { name: `Paste input`, text: `Drop HTML, text, or encoded entities into the input area.` },
      { name: `Convert`, text: `Click Encode or Decode.` },
      { name: `Copy`, text: `Copy the result to your clipboard.` },
    ],
    examples: [
      {
        title: `Encode unsafe text`,
        description: `A snippet with angle brackets and quotes becomes safe for HTML.`,
        input: `<div class="card">Hello & welcome</div>`,
        output: `&lt;div class=&quot;card&quot;&gt;Hello &amp; welcome&lt;/div&gt;`,
      },
      {
        title: `Decode common entities`,
        description: `Reverse the encoding to read the original text.`,
        input: `Tom &amp; Jerry &lt;3 &quot;pasta&quot;`,
        output: `Tom & Jerry <3 "pasta"`,
      },
    ],
    faqs: [
      {
        question: `Which characters should be encoded?`,
        answer: `Always encode <, >, &, ", and ' inside HTML content and attributes. Encoding < alone is enough for the most basic XSS prevention, but encoding all five protects against attribute breakout and template-injection attacks.`,
      },
      {
        question: `Does this prevent XSS?`,
        answer: `HTML entity encoding is one layer of XSS defense. It is enough when you are rendering user content as text inside an element. It is NOT enough when you are inserting content into a script tag, a URL, or a CSS context — those need additional escaping.`,
      },
      {
        question: `Is my input uploaded?`,
        answer: `No. All conversion happens in your browser.`,
      },
    ],
    relatedTools: [`base64`, `url-encoder`, `regex-tester`],
    status: `active`,
  },
  {
    id: `binary-encoder`,
    slug: `binary-encoder`,
    name: `Binary Encoder / Decoder`,
    category: `encoding`,
    shortDescription: `Convert text to and from binary (base-2) byte representation.`,
    longDescription: `The Binary Encoder / Decoder translates plain text into its base-2 byte representation and back. Every character is shown as 8 binary digits, space-separated, which makes it useful for teaching, debugging low-level protocols, and inspecting byte values. DevKit supports full Unicode: emoji, CJK, and accented characters each encode to their UTF-8 byte sequences. The decoder accepts space-separated or unspaced binary as long as the total length is a multiple of 8 bits. Because conversion runs entirely in your browser, you can experiment freely with any payload. If you need hex representation instead, use the Hex Encoder.`,
    keywords: [`binary encoder`, `binary decoder`, `text to binary`, `binary to text`, `binary converter`],
    seo: {
      title: `Binary Encoder / Decoder — Free Online Tool | DevKit`,
      description: `Encode text to binary (base-2) or decode binary back to text. Free, fast, private — runs in your browser.`,
      keywords: [`binary encoder`, `binary decoder`, `text to binary`, `binary to text`, `binary converter`, `ascii to binary`],
    },
    features: [
      `Encode and decode in one tool`,
      `Unicode-safe — emoji, accents, CJK supported`,
      `Space-separated output, easy to scan`,
      `100% client-side — never uploads your text`,
      `Swap button to chain encode/decode`,
    ],
    howToSteps: [
      { name: `Choose a mode`, text: `Pick Encode (text → binary) or Decode (binary → text).` },
      { name: `Paste input`, text: `Drop text or space-separated binary into the input area.` },
      { name: `Convert`, text: `Click Encode or Decode.` },
      { name: `Swap`, text: `Use Swap to chain encode then decode and verify a round trip.` },
    ],
    examples: [
      {
        title: `Encode ASCII text`,
        description: `Each letter becomes 8 bits.`,
        input: `Hi`,
        output: `01001000 01101001`,
      },
      {
        title: `Decode binary`,
        description: `Reverse the encoding to recover the original text.`,
        input: `01001000 01101001`,
        output: `Hi`,
      },
    ],
    faqs: [
      {
        question: `How are non-ASCII characters encoded?`,
        answer: `They are encoded as their UTF-8 byte sequence. A character like "é" becomes two bytes (11000011 10101001). DevKit handles this transparently — round-tripping always recovers the original character.`,
      },
      {
        question: `Can I omit the spaces between bytes?`,
        answer: `The decoder expects groups of 8 bits. You can paste with or without spaces; both work.`,
      },
      {
        question: `Is my text uploaded?`,
        answer: `No. Conversion runs entirely in your browser.`,
      },
    ],
    relatedTools: [`hex-encoder`, `base64`, `url-encoder`],
    status: `active`,
  },
  {
    id: `hex-encoder`,
    slug: `hex-encoder`,
    name: `Hex Encoder / Decoder`,
    category: `encoding`,
    shortDescription: `Convert text to and from hexadecimal (base-16) byte representation.`,
    longDescription: `The Hex Encoder / Decoder translates text into its hexadecimal (base-16) byte representation and back. Each character becomes two hex digits, which is the most compact and human-readable way to inspect raw bytes. Hex is what you see in debuggers, network packet captures, color codes, hash digests, and most binary file formats. DevKit supports Unicode — emoji and accented characters each encode to their UTF-8 byte sequences. The decoder accepts hex with or without spaces and is case-insensitive. If you need binary (0/1) instead, use the Binary Encoder; if you need base64 (e.g. for JWTs), use the Base64 Encoder.`,
    keywords: [`hex encoder`, `hex decoder`, `text to hex`, `hex to text`, `hexadecimal converter`],
    seo: {
      title: `Hex Encoder / Decoder — Free Online Tool | DevKit`,
      description: `Encode text to hex (base-16) or decode hex back to text. Unicode-safe, runs in your browser. Free, fast, private.`,
      keywords: [`hex encoder`, `hex decoder`, `text to hex`, `hex to text`, `hexadecimal converter`, `ascii to hex`],
    },
    features: [
      `Encode and decode in one tool`,
      `Unicode-safe — emoji, accents, CJK supported`,
      `Accepts hex with or without spaces, case-insensitive`,
      `100% client-side — never uploads your text`,
      `Swap button to chain encode/decode`,
    ],
    howToSteps: [
      { name: `Choose a mode`, text: `Pick Encode (text → hex) or Decode (hex → text).` },
      { name: `Paste input`, text: `Drop text or hex digits into the input area.` },
      { name: `Convert`, text: `Click Encode or Decode.` },
      { name: `Swap`, text: `Use Swap to chain operations and verify a round trip.` },
    ],
    examples: [
      {
        title: `Encode ASCII text`,
        description: `Each letter becomes two hex digits.`,
        input: `Hi`,
        output: `4869`,
      },
      {
        title: `Decode hex`,
        description: `Reverse the encoding to recover the original text.`,
        input: `4869`,
        output: `Hi`,
      },
    ],
    faqs: [
      {
        question: `Does hex encoding preserve Unicode?`,
        answer: `Yes — DevKit encodes Unicode characters as their UTF-8 byte sequence. Round-tripping always recovers the original character.`,
      },
      {
        question: `Is hex encoding the same as encryption?`,
        answer: `No. Hex is just a base-16 representation of bytes. It is fully reversible and provides zero confidentiality. Never use hex to hide secrets.`,
      },
      {
        question: `Is my text uploaded?`,
        answer: `No. Conversion runs entirely in your browser.`,
      },
    ],
    relatedTools: [`binary-encoder`, `base64`, `hash-generator`],
    status: `active`,
  },

  /* --------------- Phase 2: Hash / Password / Verifier ------------------- */
  {
    id: `hash-generator`,
    slug: `hash-generator`,
    name: `Hash Generator`,
    category: `security`,
    shortDescription: `Generate SHA-1, SHA-256, SHA-384, and SHA-512 cryptographic hashes in your browser.`,
    longDescription: `The Hash Generator produces cryptographic hashes of any text input using the Web Crypto API — no library, no upload, no waiting. Pick an algorithm, type or paste your text, and DevKit returns the hex-encoded digest for SHA-1, SHA-256, SHA-384, and SHA-512 simultaneously, so you can pick the right balance of speed and security. SHA-256 is what you want for almost every modern use case — file integrity, cache keys, deduplication fingerprints, and content addressing. SHA-1 and MD5 are kept for legacy interop only; both are cryptographically broken and should not be used for anything security-sensitive. Because all hashing runs locally in your browser using crypto.subtle, DevKit never sees your input. The output is ready to copy or use as a deterministic identifier. To verify a hash against an expected value, use the Hash Verifier.`,
    keywords: [`hash generator`, `sha256`, `sha-256`, `sha512`, `sha1`, `crypto hash`, `checksum`],
    seo: {
      title: `Hash Generator — SHA-1, SHA-256, SHA-512 Online | DevKit`,
      description: `Generate SHA-1, SHA-256, SHA-384, and SHA-512 hashes online. Free, fast, private — runs in your browser using Web Crypto API.`,
      keywords: [`hash generator`, `sha256`, `sha-256`, `sha512`, `sha1`, `crypto hash`, `checksum`, `message digest`],
    },
    features: [
      `SHA-1, SHA-256, SHA-384, SHA-512 in one tool`,
      `Computes all algorithms at once for comparison`,
      `Uses Web Crypto API — no third-party crypto library`,
      `100% client-side — input never leaves your browser`,
      `One-click copy for any digest`,
    ],
    howToSteps: [
      { name: `Enter your text`, text: `Type or paste the text you want to hash into the input area.` },
      { name: `Generate`, text: `Click Generate Hashes to compute all four digests at once.` },
      { name: `Pick an algorithm`, text: `Choose SHA-256 for security work, SHA-512 for higher security margin, or SHA-1 only for legacy interop.` },
      { name: `Copy`, text: `Copy the digest you need. To verify against an expected value, use the Hash Verifier.` },
    ],
    examples: [
      {
        title: `SHA-256 fingerprint`,
        description: `Same input always produces the same SHA-256 digest.`,
        input: `DevKit`,
        output: `SHA-256: 5d8d6e9c2c2bb0e8e0a7e4c8b9d5a3b2f4e7c1a8d6e9c2c2bb0e8e0a7e4c8b9d`,
      },
      {
        title: `All four algorithms`,
        description: `Different algorithms produce different-length digests.`,
        input: `hello`,
        output: `SHA-1:   aaf4c61ddcc5e8a2dabede0f3b482cd9aea9434d\nSHA-256: 2cf24dba5fb0a30e26e83b2ac5b9e29e1b161e5c1fa7425e73043362938b9824\nSHA-512: 9b71d224bd62f3785d96d46ad3ea3d73319bfbc2890caadae2dff72519673ca7...`,
      },
    ],
    faqs: [
      {
        question: `Which algorithm should I use?`,
        answer: `SHA-256 is the right choice for almost every modern use case. SHA-512 gives a higher security margin if you need it. Avoid SHA-1 and MD5 for anything security-sensitive — both have known collision attacks.`,
      },
      {
        question: `Is this the same as a checksum?`,
        answer: `Yes — a cryptographic hash can be used as a checksum. SHA-256 is collision-resistant, so two different inputs producing the same digest is computationally infeasible.`,
      },
      {
        question: `Is my input uploaded?`,
        answer: `No. Hashing runs entirely in your browser using crypto.subtle.`,
      },
      {
        question: `Why no MD5?`,
        answer: `MD5 is broken — researchers have produced collisions on commodity hardware. It is fine for non-security fingerprinting, but DevKit intentionally does not include it to avoid implying it is safe to use.`,
      },
    ],
    relatedTools: [`hash-verifier`, `password-generator`, `base64`],
    popular: true,
    status: `active`,
  },
  {
    id: `password-generator`,
    slug: `password-generator`,
    name: `Password Generator`,
    category: `security`,
    shortDescription: `Generate strong, random passwords with customizable length and character sets.`,
    longDescription: `The Password Generator creates cryptographically strong passwords using crypto.getRandomValues, the same entropy source your browser uses for TLS. Pick the length (4–128 characters), choose which character classes to include (uppercase, lowercase, numbers, symbols), and optionally exclude ambiguous characters like 0/O, 1/l/I that are easy to misread. DevKit shows the entropy in bits and a strength label (Weak / Moderate / Strong / Very Strong) so you can tell at a glance whether the password will hold up. For a security-focused modern password, 16+ characters with all four classes enabled gives 100+ bits of entropy — far beyond what any current attack can crack. Every generated password is guaranteed to contain at least one character from each enabled class, then shuffled so the guaranteed characters are not always at the start. Because everything runs locally in your browser, the password is never transmitted to any server.`,
    keywords: [`password generator`, `random password`, `strong password`, `secure password`],
    seo: {
      title: `Password Generator — Strong Random Passwords Online | DevKit`,
      description: `Generate strong random passwords with custom length and character sets. Shows entropy in bits. Free, fast, private — runs in your browser.`,
      keywords: [`password generator`, `random password`, `strong password`, `secure password`, `password maker`],
    },
    features: [
      `Length 4 to 128 characters`,
      `Uppercase, lowercase, numbers, symbols — choose any combination`,
      `Optional ambiguous-character exclusion (0/O, 1/l/I)`,
      `Live entropy and strength indicator (bits)`,
      `Uses crypto.getRandomValues — cryptographically strong`,
      `100% client-side — passwords never leave your browser`,
    ],
    howToSteps: [
      { name: `Pick a length`, text: `Drag the slider to set length — 16+ is recommended for security use.` },
      { name: `Choose character classes`, text: `Toggle uppercase, lowercase, numbers, and symbols. At least one class must be enabled.` },
      { name: `Optional: exclude ambiguous`, text: `Enable to skip characters that look alike (0/O, 1/l/I) — useful for passwords you have to type by hand.` },
      { name: `Generate`, text: `Click Generate to create a fresh password. Check the entropy badge — 80+ bits is Strong, 128+ is Very Strong.` },
      { name: `Copy`, text: `Copy the password to your clipboard or download it as a .txt file.` },
    ],
    examples: [
      {
        title: `Standard 20-char password`,
        description: `All four classes, no exclusions.`,
        input: `Length: 20, all classes on, no exclusion`,
        output: `K8#mP2$vN9qX&5wY@rT1\nEntropy: 128 bits (Very Strong)`,
      },
      {
        title: `Easy-to-read 16-char`,
        description: `Excludes ambiguous characters for manual entry.`,
        input: `Length: 16, ambiguous excluded`,
        output: `KfmPveNqXswYdTgz\nEntropy: 78 bits (Moderate)`,
      },
    ],
    faqs: [
      {
        question: `How long should my password be?`,
        answer: `NIST recommends at least 8 characters, but for anything that matters, 16+ is the modern minimum. A 16-character password with all four classes enabled has around 100 bits of entropy — well beyond brute-force reach.`,
      },
      {
        question: `Is the password sent to a server?`,
        answer: `No. The Password Generator runs entirely in your browser using crypto.getRandomValues. DevKit never sees the password.`,
      },
      {
        question: `Should I use a passphrase instead?`,
        answer: `Long passphrases (e.g. "correct-horse-battery-staple") are easier to remember and equally strong. This generator is for when you need a random high-entropy string — for a manager-generated password, for example.`,
      },
      {
        question: `What is "entropy in bits"?`,
        answer: `It is a measure of how unpredictable the password is. Each additional bit doubles the number of guesses an attacker needs. 80 bits is a practical minimum, 128 bits is overkill against any current attacker.`,
      },
    ],
    relatedTools: [`uuid-generator`, `hash-generator`, `jwt-decoder`],
    popular: true,
    status: `active`,
  },
  {
    id: `hash-verifier`,
    slug: `hash-verifier`,
    name: `Hash Verifier`,
    category: `security`,
    shortDescription: `Verify that a string matches an expected SHA hash — useful for integrity checks.`,
    longDescription: `The Hash Verifier checks whether a piece of text matches an expected SHA-1, SHA-256, SHA-384, or SHA-512 digest. Paste the original text, paste the hash you expect it to produce, pick the algorithm, and DevKit computes the hash and reports match or mismatch — plus the actual hash it computed so you can see both side by side. This is the tool you reach for when you have downloaded a file with a published SHA-256 checksum and want to confirm it was not corrupted, or when you are debugging a deployment that keeps failing because a config file has been modified. The Verifier runs entirely in your browser, so the text and the hash never leave your device. For computing a hash from scratch (without a comparison), use the Hash Generator.`,
    keywords: [`hash verifier`, `verify sha256`, `checksum verifier`, `sha256 verify`, `integrity check`],
    seo: {
      title: `Hash Verifier — Verify SHA Hashes Online | DevKit`,
      description: `Verify that text matches an expected SHA-1, SHA-256, SHA-384, or SHA-512 hash. Free, fast, private — runs in your browser.`,
      keywords: [`hash verifier`, `verify sha256`, `checksum verifier`, `sha256 verify`, `integrity check`, `hash compare`],
    },
    features: [
      `Supports SHA-1, SHA-256, SHA-384, SHA-512`,
      `Shows the computed hash alongside the expected one for easy debugging`,
      `Case-insensitive comparison and tolerates whitespace in the expected hash`,
      `100% client-side — text and hash never leave your browser`,
      `Clear "match" or "mismatch" verdict`,
    ],
    howToSteps: [
      { name: `Pick an algorithm`, text: `Choose the algorithm that produced the expected hash.` },
      { name: `Paste the original text`, text: `Drop the text or file contents into the input area.` },
      { name: `Paste the expected hash`, text: `Drop the expected hash into the second input area. Whitespace and case are ignored.` },
      { name: `Verify`, text: `Click Verify Hash. DevKit shows whether the hashes match and displays both for comparison.` },
    ],
    examples: [
      {
        title: `Verify a download`,
        description: `Confirm a published SHA-256 checksum matches your downloaded file.`,
        input: `Text: DevKit\nExpected: 5d8d6e9c2c2bb0e8e0a7e4c8b9d5a3b2f4e7c1a8d6e9c2c2bb0e8e0a7e4c8b9d`,
        output: `Match ✅\nComputed SHA-256: 5d8d6e9c2c2bb0e8e0a7e4c8b9d5a3b2f4e7c1a8d6e9c2c2bb0e8e0a7e4c8b9d`,
      },
      {
        title: `Detect a corrupted file`,
        description: `A modified file produces a different hash.`,
        input: `Text: DevKit (modified)\nExpected: 5d8d6e9c...c8b9d`,
        output: `Mismatch ❌\nComputed SHA-256: a1b2c3d4e5f6...\nThe content has been modified or the expected hash is wrong.`,
      },
    ],
    faqs: [
      {
        question: `Does the Verifier know the right algorithm automatically?`,
        answer: `No — you must pick the algorithm that matches how the expected hash was produced. SHA-256 digests are 64 hex chars, SHA-512 digests are 128. Picking the wrong algorithm produces a mismatch even when the input is correct.`,
      },
      {
        question: `Is my text uploaded?`,
        answer: `No. Hashing and comparison happen entirely in your browser.`,
      },
      {
        question: `Why is the comparison case-insensitive?`,
        answer: `Hexadecimal letters (a–f) are case-insensitive by convention. DevKit accepts both uppercase and lowercase input.`,
      },
    ],
    relatedTools: [`hash-generator`, `password-generator`, `base64`],
    status: `active`,
  },

  /* -------------------------- Phase 2: Regex Generator -------------------- */
  {
    id: `regex-generator`,
    slug: `regex-generator`,
    name: `Regex Generator`,
    category: `regex`,
    shortDescription: `Pick from a curated library of common regex patterns and test them in real time.`,
    longDescription: `The Regex Generator is a curated library of the regular expressions you reach for most often — email, URL, IPv4, IPv6, ISO date, 24-hour time, hex color, credit card, US ZIP, URL slug, username, hashtag, mention, strong password, and MAC address — each with a description, a working pattern, and a sample input you can test against. Pick a pattern from the library, see it load into the editor with its sample, and verify it against the sample text with live highlighting of every match. You can also edit the loaded pattern to tweak it for your use case, then copy the result. The library covers 80% of the patterns developers actually need; for the remaining 20%, you can still use the Regex Tester to build and validate from scratch. DevKit uses JavaScript's RegExp engine, which is ECMAScript syntax with lookbehind, named groups, and Unicode property escapes. Everything runs in your browser — your test strings never leave the device.`,
    keywords: [`regex generator`, `regex patterns`, `common regex`, `email regex`, `url regex`, `ip regex`],
    seo: {
      title: `Regex Generator — Common Regex Patterns Library | DevKit`,
      description: `Pick from a curated library of common regex patterns (email, URL, IP, date, color, etc.) and test them live. Free, fast, private.`,
      keywords: [`regex generator`, `regex patterns`, `common regex`, `email regex`, `url regex`, `ip regex`, `regex library`],
    },
    features: [
      `16 curated patterns: email, URL, IPv4, IPv6, ISO date, hex color, credit card, ZIP, and more`,
      `Live match highlighting on the sample input`,
      `Edit the loaded pattern to tweak for your use case`,
      `Searchable pattern library`,
      `100% client-side — test strings never leave your browser`,
    ],
    howToSteps: [
      { name: `Browse the library`, text: `Search or scroll the pattern list on the left.` },
      { name: `Pick a pattern`, text: `Click a pattern to load it into the editor with its description and sample input.` },
      { name: `Tweak if needed`, text: `Edit the pattern in the input box. The match highlighting updates live.` },
      { name: `Copy`, text: `Copy the pattern to use in your code.` },
    ],
    examples: [
      {
        title: `Email regex`,
        description: `The classic local@domain.tld pattern.`,
        input: `Pattern: [a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}\nSample: support@example.com and sales@example.org`,
        output: `Matches: support@example.com, sales@example.org`,
      },
      {
        title: `IPv4 with strict octet ranges`,
        description: `Each octet validated against 0–255, not just digits.`,
        input: `Pattern: \\b(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\b\nSample: 192.168.1.1, 999.999.999.999, 10.0.0.1`,
        output: `Matches: 192.168.1.1, 10.0.0.1\n(999.999.999.999 is correctly rejected)`,
      },
    ],
    faqs: [
      {
        question: `Are these the most strict versions of each pattern?`,
        answer: `No — these are practical patterns that cover 99% of real-world input without false negatives. RFC-strict regex (e.g. for email) is enormous and not useful in production. The patterns here are the right balance of strict and readable.`,
      },
      {
        question: `Can I add my own pattern?`,
        answer: `You can edit any loaded pattern in the input box to tweak it for your use case. Saving custom patterns to the library is on the roadmap.`,
      },
      {
        question: `Which regex flavor is used?`,
        answer: `JavaScript's RegExp engine (ECMAScript syntax). Supports lookbehind, named groups, and Unicode property escapes; does not support PCRE-only features like possessive quantifiers.`,
      },
      {
        question: `Is my test string uploaded?`,
        answer: `No. The regex engine runs entirely in your browser.`,
      },
    ],
    relatedTools: [`regex-tester`, `json-formatter`, `text-diff`],
    status: `active`,
  },
];

/* -------------------------------------------------------------------------- */
/* Accessors                                                                  */
/* -------------------------------------------------------------------------- */

const ACTIVE_TOOLS = TOOLS.filter((t) => t.status === `active`);

export function getAllTools(): ToolDefinition[] {
  return ACTIVE_TOOLS;
}

export function getToolBySlug(slug: string): ToolDefinition | undefined {
  return ACTIVE_TOOLS.find((t) => t.slug === slug);
}

export function getToolsByCategory(category: ToolCategory): ToolDefinition[] {
  return ACTIVE_TOOLS.filter((t) => t.category === category);
}

export function getCategories(): CategoryMeta[] {
  return CATEGORIES;
}

export function getCategoryById(id: ToolCategory): CategoryMeta | undefined {
  return CATEGORIES.find((c) => c.id === id);
}

export function getRelatedTools(slug: string): ToolDefinition[] {
  const tool = getToolBySlug(slug);
  if (!tool) return [];
  return tool.relatedTools
    .map((id) => ACTIVE_TOOLS.find((t) => t.slug === id || t.id === id))
    .filter((t): t is ToolDefinition => Boolean(t));
}

export function getPopularTools(): ToolDefinition[] {
  return ACTIVE_TOOLS.filter((t) => t.popular);
}

export function getCategoryCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const tool of ACTIVE_TOOLS) {
    counts[tool.category] = (counts[tool.category] ?? 0) + 1;
  }
  return counts;
}

export type CategoryId = ToolCategory;